/* eslint-disable eslint-comments/no-restricted-disable, max-lines -- Existing dropdown exceeds the limit; this PR only migrates its QuickPost hook usage. */
import { BookmarkButton } from "@circle-react-shared/Bookmarks/BookmarkButton";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { useModalData } from "@circle-react-uikit/ModalV2";
import { postApi } from "@circle-react/api/postApi";
import { useDuplicateImagePostModal } from "@circle-react/components/Modals/DuplicateImagePostModal";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { useEditPostModal } from "@circle-react/components/QuickPost";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { hasFeaturedAreas } from "@circle-react/helpers/communityHelpers";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { flaggedContent } from "@circle-react/helpers/urlHelpers";
import type { Post } from "@circle-react/types/Post";
import { noop } from "lodash";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { useImagePostModal } from "@/react/components/Modals/ImagePostModal";
import { useConvertPostToBroadcast } from "@/react/components/QuickPostV2/useConvertPostToBroadcast";
import { Badge } from "@/react/components/SettingsApp/CompassCompat/Badge";
import { ConfirmationModal } from "@/react/components/SettingsApp/CompassCompat/ConfirmationModal";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { isBasicPost, isImagePost } from "@/react/helpers/postHelpers";
import type { CurrentCommunity } from "@/react/types/CurrentCommunity";

import { PostShareOptions } from "../../PostShareOptions";
import {
  CloseCommentsAction,
  DisableCommentsAction,
  FeaturedAreaVisibilityAction,
  FollowAction,
  PinToSidebarAction,
  PinToTopAction,
} from "../Actions";
import { DisableLikesAction } from "../DisableLikesAction";
import { DuplicatePostModal } from "../DuplicatePostModal";
import { useReportPostModal } from "../ReportPostModal";
import { CardViewThumbnailModal } from "./CardViewThumbnailModal";
import { usePostDropdownActions } from "./usePostDropdownActions";

export interface PostDropdownActionsProps {
  currentCommunity?: CurrentCommunity;
  onChange: (post?: Post) => void;
  onPostDestroy: (post: Post) => void;
  refetchPosts?: () => void;
}

export const PostDropdownActions = ({
  refetchPosts,
  currentCommunity,
  onPostDestroy,
  onChange,
}: PostDropdownActionsProps) => {
  const {
    post,
    showModerationInActionsDropdown: shouldShowModerationInActionsDropdown,
    canManagePost,
    canUpdatePost,
    canDestroyPost,
    canDuplicatePost,
    canReportPost,
    refetchPostDetails,
    isTiptapPost,
  } = useCurrentPostHelpers();
  const { usedIn, isUsedInMinimalTipTapViewModal } = useCurrentPostContext();
  const {
    currentCommunitySettings,
    isViewOnlyMasquerading,
    currentCommunityMember,
  } = usePunditUserContext();
  const { show: showEditPostModal } = useEditPostModal();
  const imagePostModal = useImagePostModal();
  const duplicateImagePostModal = useDuplicateImagePostModal();
  const reportPostModal = useReportPostModal();
  const isPostImagePost = isImagePost(post);
  const isPostBasicPost = isBasicPost(post);
  const { contentPortalElement, onClose: onCloseCurrentModal } = useModalData();
  const { data: space } = useCurrentSpaceContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const isKbPostActionEnabled =
    currentCommunitySettings?.kb_post_action_enabled && isAdmin;
  const { onShareViaBroadcast, isPending: isSharingViaBroadcast } =
    useConvertPostToBroadcast();
  const { success, error } = useToast();
  const updateKnowledgeBasePostMutation = useMutation({
    mutationFn: () => postApi.updateKnowledgeBasePost({ post }),
    onSuccess: () => {
      success(t("post_dropdown_actions.update_knowledge_base_post_successful"));
    },
    onError: () =>
      error(t("post_dropdown_actions.update_knowledge_base_post_failed")),
  });

  const {
    shouldShowPinToSidebar,
    shouldShowShareOnWebOptions,
    shouldShowSetThumbnailOptionForPosts,
    shouldShowDuplicatePostModal,
    toggleDuplicatePostModal,
    shouldShowConfirmationModal,
    toggleConfirmationModal,
    deleteMutation,
    redirectToPost,
    shouldShowSetThumbnailModal,
    toggleSetThumbnailModal,
  } = usePostDropdownActions({ onPostDestroy });

  if (isViewOnlyMasquerading) {
    return (
      <IconButton
        name="16-menu-dots-horizontal"
        iconClassName="!text-dark cursor-not-allowed"
        ariaLabel={t("post_actions")}
        dataTestId="action-more"
        disabled
      />
    );
  }

  return (
    <>
      <Dropdown
        direction="bottom-end"
        menuButtonEl="div"
        button={
          <IconButton
            name="20-dot-menu"
            iconClassName="!text-default group-hover:!text-dark"
            className="group"
            variant="secondary"
            iconSize={20}
            buttonSize={isUsedInMinimalTipTapViewModal ? 32 : 28}
            ariaLabel={t("post_actions")}
            dataTestId="action-more"
          />
        }
        className={classNames("z-10", {
          "inline-flex h-8 w-8": isUsedInMinimalTipTapViewModal,
        })}
        transitionWrapperClassName="z-50"
        size="large"
        buttonLabel={t("post_actions")}
        appendTo={contentPortalElement ?? document.body}
      >
        <BookmarkButton
          variant="dropdown"
          bookmarkType="post"
          record={post}
          onSuccess={refetchPostDetails}
        />
        {shouldShowShareOnWebOptions && (
          <PostShareOptions
            onChange={onChange}
            button={
              <Dropdown.ItemLink>
                {t("post.share.share_post_actions_label")}
              </Dropdown.ItemLink>
            }
            shouldRenderCustomMenuButton
          />
        )}
        {canUpdatePost && isPostBasicPost && (
          <Dropdown.ItemLink
            onClick={() => {
              if (isViewOnlyMasquerading) {
                noop();
              } else {
                void showEditPostModal({
                  post,
                  onPostChange: onChange,
                  shouldSkipRedirectAfterSubmit: isUsedInMinimalTipTapViewModal,
                });
              }
            }}
          >
            {t("edit_post")}
          </Dropdown.ItemLink>
        )}
        {canUpdatePost && isPostImagePost && (
          <Dropdown.ItemLink
            onClick={() => {
              void imagePostModal.show({
                spaceId: space?.id,
                onClose: () => imagePostModal.remove(),
                onPostChange: onChange,
                currentPost: post,
                shouldSkipRedirectAfterSubmit: usedIn === "full-post-modal",
              });
            }}
          >
            {t("edit_post")}
          </Dropdown.ItemLink>
        )}
        {isPostBasicPost && canDuplicatePost && (
          <Dropdown.ItemLink forceClose onClick={toggleDuplicatePostModal}>
            {t("duplicate_post")}
          </Dropdown.ItemLink>
        )}
        {isPostImagePost && canDuplicatePost && (
          <Dropdown.ItemLink
            onClick={() => {
              if (usedIn === "full-post-modal") {
                onCloseCurrentModal();
              }
              void duplicateImagePostModal.show({
                post,
                onSuccess: redirectToPost,
              });
            }}
          >
            {t("duplicate_post")}
          </Dropdown.ItemLink>
        )}
        {shouldShowSetThumbnailOptionForPosts && (
          <Dropdown.ItemLink
            onClick={() => {
              toggleSetThumbnailModal();
            }}
          >
            {t("set_thumbnail")}
          </Dropdown.ItemLink>
        )}
        {canReportPost && (
          <Dropdown.ItemLink
            onClick={
              isViewOnlyMasquerading
                ? noop
                : () => {
                    void reportPostModal.show({ post });
                  }
            }
          >
            {t("report_post")}
          </Dropdown.ItemLink>
        )}
        {shouldShowModerationInActionsDropdown && (
          <Dropdown.ItemWithLink to={flaggedContent.index()}>
            {t("flagged_contents.review")}
          </Dropdown.ItemWithLink>
        )}
        {canDestroyPost && (
          <Dropdown.ItemLink
            onClick={isViewOnlyMasquerading ? noop : toggleConfirmationModal}
          >
            <span className="text-v2-danger">{t("delete_post")}</span>
          </Dropdown.ItemLink>
        )}
        {isKbPostActionEnabled && (
          <Dropdown.ItemLink
            onClick={() => {
              updateKnowledgeBasePostMutation.mutate();
            }}
          >
            {t(
              "post_dropdown_actions.update_knowledge_base_post_dropdown_label",
            )}
          </Dropdown.ItemLink>
        )}
        {isAdmin && isPostBasicPost && isTiptapPost && (
          <>
            <Dropdown.Separator />
            <Dropdown.ItemLink
              data-testid="send-announcement-post-action"
              disabled={isSharingViaBroadcast}
              onClick={() => onShareViaBroadcast({ postId: post.id })}
            >
              <div className="flex items-center justify-between gap-2">
                {t("post_dropdown_actions.share_via_broadcast")}
                <Badge label={t("new")} square variant="secondary" />
              </div>
            </Dropdown.ItemLink>
          </>
        )}
        {(canUpdatePost ||
          canDuplicatePost ||
          canReportPost ||
          shouldShowModerationInActionsDropdown ||
          canDestroyPost) && <Dropdown.Separator />}
        <FollowAction post={post} />
        {canManagePost && (
          <>
            <PinToTopAction
              post={post}
              onChange={onChange}
              refetchPosts={refetchPosts}
            />
            {shouldShowPinToSidebar && (
              <PinToSidebarAction post={post} onChange={onChange} />
            )}
            <DisableLikesAction post={post} onChange={onChange} />
            <DisableCommentsAction post={post} onChange={onChange} />
            {post.is_comments_enabled && (
              <CloseCommentsAction post={post} onChange={onChange} />
            )}
            {!!currentCommunity && hasFeaturedAreas(currentCommunity) && (
              <FeaturedAreaVisibilityAction
                post={post}
                onChange={onChange}
                refetchPosts={refetchPosts}
              />
            )}
          </>
        )}
      </Dropdown>
      <CardViewThumbnailModal
        post={post}
        onChange={onChange}
        onClose={toggleSetThumbnailModal}
        isOpen={shouldShowSetThumbnailModal}
      />
      <DuplicatePostModal
        spaceId={post.space_id}
        postId={post.id}
        postSlug={post.slug}
        postTitle={post.name}
        isOpen={shouldShowDuplicatePostModal}
        onClose={toggleDuplicatePostModal}
      />
      <ConfirmationModal
        isOpen={shouldShowConfirmationModal}
        title={t("delete_post_confirmation")}
        onClose={toggleConfirmationModal}
        onCancel={toggleConfirmationModal}
        onConfirm={deleteMutation.mutate}
        confirmVariant="danger"
        disabled={deleteMutation.isPending}
        confirmText={deleteMutation.isPending ? t("deleting") : t("confirm")}
      >
        {t("delete_post_descriptor")}
      </ConfirmationModal>
    </>
  );
};
